import './ClientAuthLinkModal.scss';
import './XpektorBootstrapModal.scss';
import Moment from 'moment';
import { Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
  faCircleXmark,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataPanel from '../../../Components/DataPanel/DataPanel';
import AuthService from '../../../Services/AuthService';
import XpektorButton from '../../../Components/XpektorButton/XpektorButton';
import LoadingBar from '../../../Components/LoadingBar/LoadingBar';

/* eslint-disable react/prop-types */

function OnboardingEvent({ event }) {
  return (
    <div className="event-entry">
      <div className="title-row">
        <div className="title-content">
          <p className="event-title">{event.title}</p>
          <FontAwesomeIcon
            className={`event-type ${event.type}`}
            icon={
              event.type === 'redirect'
                ? faArrowUpRightFromSquare
                : event.type === 'success'
                ? faCircleCheck
                : event.type === 'error'
                ? faCircleXmark
                : ''
            }
          />
        </div>
        <p>{Moment(event.date).format('YYYY-MM-DD HH:mm')}</p>
      </div>
      <p className="event-action">{event.action}</p>
      <p>
        <b>{event.type === 'error' ? 'Error meddelande:' : ''} </b>
        {event?.error ?? ''}
      </p>
    </div>
  );
}

const gsT = (t, key) => t(`shared${key}`);

export default function ClientAuthLinkModal({ client, handleClose, t }) {
  const [externalAuthState, setExternalAuthState] = useState(null);
  const [events, setEvents] = useState([]);
  const [authUrl, setAuthUrl] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [creatingLink, setCreatingLink] = useState(false);

  const [animatingCopied, setAnimatingCopied] = useState(false);
  const [animatingCopiedCount, setAnimatingCopiedCount] = useState(0);

  useEffect(() => {
    const getExternalAuthState = async () => {
      await AuthService.getExtendedExternalAuthState(client.id)
        .then((response) => {
          if (response.data) {
            setExternalAuthState(response.data);
            setAuthUrl(
              `${window.location.protocol}//${window.location.host}/authorization?state=${response.data.id}`
            );

            const startEvents = response.data.events
              .filter((event) => event.eventDescription !== 'Bjorn Lunden')
              .map((event) => {
                return {
                  id: `${event.id}-start`,
                  title:
                    event.onboardingStep === 1
                      ? 'Bokföring'
                      : event.onboardingStep === 2
                      ? 'Bank'
                      : event.onboardingStep === 3
                      ? 'Skatteverket'
                      : 'N/A',
                  action:
                    event.onboardingStep !== 3
                      ? `Omdirigerad till ${event.eventDescription}`
                      : 'Öppnade skatteverket fliken',
                  date: event.startDate,
                  type: 'redirect',
                  error: '',
                };
              });

            const endEvents = response.data.events
              .filter((event) => event.endDate !== null)
              .map((event) => {
                return {
                  id: `${event.id}-end`,
                  title:
                    event.onboardingStep === 1
                      ? 'Bokföring'
                      : event.onboardingStep === 2
                      ? 'Bank'
                      : event.onboardingStep === 3
                      ? 'Skatteverket'
                      : 'N/A',
                  action:
                    event.onboardingStep !== 3
                      ? event?.success === true
                        ? `Kopplade ${event.eventDescription}`
                        : `Kunde inte koppla ${event.eventDescription}`
                      : event?.success === true
                      ? 'Systemet verifierade kopplingen till Skatteverket'
                      : 'Systemet hittade ingen koppling till Skatteverket',
                  date: event.endDate,
                  type: event?.success === true ? 'success' : 'error',
                  error:
                    event?.errorDescription?.length > 0
                      ? event.errorDescription
                      : '',
                };
              });
            const newEvents = [...startEvents, ...endEvents].sort(
              (a, b) => new Date(b.date) - new Date(a.date)
            );
            setEvents(newEvents);
          }
        })
        .catch(() => {})
        .finally(() => {
          setFetching(false);
        });
    };
    if (Object.keys(client).length !== 0) {
      getExternalAuthState();
    }
  }, [client]);

  useEffect(() => {
    const animateCopied = () => {
      const newAnimatingCopiedCount = animatingCopiedCount + 1;
      if (newAnimatingCopiedCount === 2) {
        setAnimatingCopiedCount(0);
        setAnimatingCopied(false);
      } else {
        setAnimatingCopiedCount(newAnimatingCopiedCount);
      }
    };
    const timer = setTimeout(() => animatingCopied && animateCopied(), 1000);
    return () => clearTimeout(timer);
  }, [animatingCopiedCount, animatingCopied]);

  const createLink = async () => {
    setCreatingLink(true);
    await AuthService.createExternalAuthState(client.id, null)
      .then((response) => {
        setExternalAuthState(response.data);
        setAuthUrl(
          `${window.location.protocol}//${window.location.host}/authorization?state=${response.data.id}`
        );
      })
      .catch(() => {})
      .finally(() => {
        setCreatingLink(false);
      });
  };

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop="static"
      centered
      keyboard={false}
      className="border-0 xpektor-bootstrap-modal client-auth-link-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {`${t('modal.client-auth-link.modal-title-prefix')} ${client.name}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching === true ? (
          <LoadingBar relative active content={gsT(t, '.actions.fetching')} />
        ) : externalAuthState === null ? (
          <div className="auth-link-missing">
            <p>{t('modal.client-auth-link.auth-link-missing.description')}</p>
            <XpektorButton
              loading={creatingLink}
              content={t('modal.client-auth-link.auth-link-missing.btn-label')}
              onClick={() => {
                createLink();
              }}
            />
          </div>
        ) : (
          <div className="auth-state-display">
            <p className="auth-state-text">
              <p className="auth-state-text">
                Skapad:{' '}
                <b>
                  {externalAuthState.dateCreated
                    ? Moment(externalAuthState.dateCreated).format(
                        'YYYY-MM-DD HH:mm'
                      )
                    : gsT(t, '.labels.not-available')}
                </b>
              </p>
              Senast använd:{' '}
              <b>
                {externalAuthState.lastAccessed
                  ? Moment(externalAuthState.lastAccessed).format(
                      'YYYY-MM-DD HH:mm'
                    )
                  : gsT(t, '.labels.not-available')}
              </b>
            </p>
            <DataPanel
              title="Användningshistorik"
              titleSize="s"
              minHeight={400}
              maxHeight={400}
              scroll
            >
              <div className="onboarding-event-list">
                {events.length > 0 ? (
                  events.map((event) => {
                    return <OnboardingEvent key={event.id} event={event} />;
                  })
                ) : (
                  <p className="no-events">Finns ingen historik</p>
                )}
              </div>
            </DataPanel>
            <div className="auth-link-actions">
              <XpektorButton
                content={
                  animatingCopied ? (
                    <FontAwesomeIcon
                      className="copied-check-icon"
                      icon={faCheck}
                    />
                  ) : (
                    t('modal.client-auth-link.copy-link')
                  )
                }
                onClick={() => {
                  navigator.clipboard.writeText(authUrl);
                  setAnimatingCopied(true);
                }}
              />
              <XpektorButton
                content={t('modal.client-auth-link.open-in-new-tab')}
                onClick={() =>
                  window.open(authUrl, '_blank', 'noopener,noreferrer')
                }
              />
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
